/* CS cart drawer: opent na ATC (fetch, geen redirect) en ververst via de
   Section Rendering API. Alle mutaties lopen serieel door één queue. Sinds
   2-9-2026 zonder cadeau-reconciliatie: er is geen abonnement meer in de
   funnel, dus ook geen e-book of slaapmasker dat aan een plan hangt. */
(() => {
  if (window.__csCartDrawerInit) return;
  window.__csCartDrawerInit = true;

  /* Zonder <dialog>-ondersteuning (iOS < 15.4) geen CSCart: de PDP post het
     formulier dan native naar /cart en het cart-icoon blijft een gewone link.
     Zonder deze guard gooide showModal() een fout nadat het formulier al was
     onderschept, en bleef de knop uitgeschakeld zonder dat er iets gebeurde. */
  if (typeof HTMLDialogElement !== 'function' || typeof HTMLDialogElement.prototype.showModal !== 'function') return;

  const routes = window.routes || {};
  const cartUrl = routes.cart_url || '/cart';
  const addUrl = (routes.cart_add_url || '/cart/add') + '.js';
  const changeUrl = (routes.cart_change_url || '/cart/change') + '.js';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const staat = {
    queue: Promise.resolve(),
    busy: 0,
    opener: null,
    aanHetSluiten: false,
    laden: 0,
  };

  const dialog = () => document.querySelector('[data-cs-cart-drawer]');
  const paneel = () => document.querySelector('[data-cs-drawer-paneel]');

  /* ---------- Cart-API ---------- */

  /* Elke cart-call krijgt een harde grens. Zonder grens blijft bij een
     hangende verbinding de skeleton staan en wacht de hele wachtrij (ook het
     cart-icoon) op een antwoord dat niet komt. Na de grens loopt het gewone
     foutpad: melding tonen en de echte cart opnieuw ophalen. */
  const FETCH_MS = 15000;
  const fetchMetGrens = (url, opties) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_MS);
    return fetch(url, Object.assign({}, opties, { signal: controller.signal })).finally(() => clearTimeout(timer));
  };

  const fetchCart = () =>
    fetchMetGrens(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => {
      if (!r.ok) throw new Error(`cart.js ${r.status}`);
      return r.json();
    });

  const post = (url, payload) =>
    fetchMetGrens(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then((r) => {
      if (!r.ok) {
        return r.json().catch(() => ({})).then((data) => {
          const fout = new Error(data.description || data.message || `HTTP ${r.status}`);
          fout.status = r.status;
          throw fout;
        });
      }
      return r.json();
    });

  const postForm = (url, formData) =>
    fetchMetGrens(url, { method: 'POST', headers: { Accept: 'application/json' }, body: formData }).then((r) => {
      if (!r.ok) {
        return r.json().catch(() => ({})).then((data) => {
          throw new Error(data.description || data.message || `HTTP ${r.status}`);
        });
      }
      return r.json();
    });

  /* ---------- Cadeau bij het volle pakket (10-9-2026) ----------
     Het slaapmasker (€0,00 in Shopify, vergelijkingsprijs €19,99) hoort in de
     cart zodra er `vanaf` verpakkingen van het hoofdproduct in staan, en weer
     weg daaronder. Alleen regels met de eigenschap _cs_cadeau worden
     verwijderd of teruggezet naar 1; een los gekocht masker blijft met rust.
     Geeft true terug als de cart gewijzigd is (dan opnieuw renderen). */
  const cadeauConfig = () => {
    const d = dialog();
    if (!d) return null;
    const variant = parseInt(d.dataset.cadeauVariant, 10) || 0;
    const vanaf = parseInt(d.dataset.cadeauVanaf, 10) || 0;
    const hoofd = parseInt(d.dataset.hoofdproduct, 10) || 0;
    if (!variant || !vanaf || !hoofd) return null;
    return { variant, vanaf, hoofd };
  };

  const reconcileCadeau = async (cart) => {
    const cfg = cadeauConfig();
    if (!cfg || !cart || !Array.isArray(cart.items)) return false;
    const packs = cart.items
      .filter((i) => i.product_id === cfg.hoofd)
      .reduce((som, i) => som + i.quantity, 0);
    const maskers = cart.items.filter((i) => i.variant_id === cfg.variant);
    const cadeaus = maskers.filter((i) => i.properties && i.properties._cs_cadeau);
    let gewijzigd = false;

    if (packs >= cfg.vanaf) {
      if (maskers.length === 0) {
        try {
          await post(addUrl, { items: [{ id: cfg.variant, quantity: 1, properties: { _cs_cadeau: '1' } }] });
          gewijzigd = true;
        } catch (fout) {
          /* Uitverkocht of niet meer gepubliceerd: stil overslaan, de
             bestelling zelf mag hier niet op stuklopen. */
          console.warn('CS cart: cadeau niet toegevoegd', fout);
        }
      } else {
        for (const regel of cadeaus) {
          if (regel.quantity > 1) {
            await post(changeUrl, { id: regel.key, quantity: 1 });
            gewijzigd = true;
          }
        }
      }
    } else if (cadeaus.length > 0) {
      for (const regel of cadeaus) {
        await post(changeUrl, { id: regel.key, quantity: 0 });
        gewijzigd = true;
      }
    }
    return gewijzigd;
  };

  /* ---------- Verversen via Section Rendering API ---------- */

  const sectieIds = 'cs-cart-drawer,cart-icon-bubble';

  /* Let op: géén Accept: application/json meesturen — Shopify geeft /cart dan
     de AJAX-cart-JSON terug in plaats van de gevraagde sections-map.
     `vooraf` is een sections-map die met een cart-mutatie meekwam; alleen
     bruikbaar zolang er daarna geen mutaties meer zijn geweest. */
  const ververs = async (vooraf) => {
    let secties = vooraf;
    if (!secties) {
      const respons = await fetchMetGrens(`${cartUrl}?sections=${sectieIds}`);
      if (!respons.ok) throw new Error(`sections ${respons.status}`);
      secties = await respons.json();
    }

    /* Niet verwisselen terwijl het paneel nog beweegt; zie slideKlaar. */
    await slideKlaar;

    const parser = new DOMParser();

    const drawerHtml = secties['cs-cart-drawer'];
    const inner = document.querySelector('[data-cs-drawer-inner]');
    if (drawerHtml && inner) {
      const nieuw = parser
        .parseFromString(drawerHtml, 'text/html')
        .querySelector('[data-cs-drawer-inner]');
      if (nieuw) {
        const body = inner.querySelector('[data-cs-drawer-body]');
        const scrollTop = body ? body.scrollTop : 0;
        const hadFocusBinnen = inner.contains(document.activeElement);
        const focusId = document.activeElement && document.activeElement.dataset
          ? document.activeElement.dataset.csFocus
          : null;

        /* De teller staat als attribuut op de inner zelf; innerHTML-swap
           neemt hem niet mee en de badge-sync leest hem. */
        if (nieuw.dataset.csTeller !== undefined) inner.dataset.csTeller = nieuw.dataset.csTeller;

        /* Skeleton naar inhoud was een harde display-wissel: het paneel schoof
           leeg binnen en de inhoud sprong er daarna in. Nu vervaagt de inhoud
           in over 160ms, zodat het aankomen van de regels leest als settelen
           in plaats van als een tweede sprong. */
        const zachtWisselen = !reducedMotion.matches;
        if (zachtWisselen) inner.classList.add('is-wisselen');
        inner.innerHTML = nieuw.innerHTML;
        if (zachtWisselen) {
          /* Zelfde vangnet: zonder rAF zou de inhoud op opacity 0 blijven
             staan en lijkt de winkelwagen leeg. */
          const toon = () => inner.classList.remove('is-wisselen');
          requestAnimationFrame(() => requestAnimationFrame(toon));
          setTimeout(toon, 120);
        }



        const nieuweBody = inner.querySelector('[data-cs-drawer-body]');
        if (nieuweBody) nieuweBody.scrollTop = scrollTop;
        if (focusId || hadFocusBinnen) {
          const doel = focusId ? inner.querySelector(`[data-cs-focus="${focusId}"]`) : null;
          /* Fallback naar de sluitknop: zonder herstel valt de focus op body
             en raakt een toetsenbordgebruiker de dialogcontext kwijt. */
          const fallback = inner.querySelector('[data-cs-focus="sluit"]');
          const focusDoel = doel && !doel.disabled ? doel : fallback;
          const d = dialog();
          if (focusDoel && d && d.open) focusDoel.focus();
        }

        meldStatus(inner);
      }
    }

    const bubbleHtml = secties['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubbleHtml && bubble) {
      const wrapper = parser.parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
      if (wrapper) bubble.innerHTML = wrapper.innerHTML;
    }
    syncBadge();

    zetGarantieTrigger();

    document.dispatchEvent(new CustomEvent('cs:cart:ververst'));
  };

  /* De garantieregel wordt alleen een knop wanneer de pop-up ook echt in de
     DOM staat (productpagina). Op andere pagina's blijft het platte tekst, dus
     geen knop die nergens toe leidt. Draait na elke render, want de innerHTML
     wordt bij elke verversing vervangen. */
  const zetGarantieTrigger = () => {
    const knop = document.querySelector('[data-cs-garantie-uitleg]');
    const statisch = document.querySelector('[data-cs-garantie-statisch]');
    if (!knop || !statisch) return;
    const beschikbaar = !!window.CSGarantie;
    knop.hidden = !beschikbaar;
    statisch.hidden = beschikbaar;
  };


  /* Badge toont dezelfde teller als de drawer-kop. De verborgen
     screenreader-tekst van het cart-icoon telt mee, anders spreken badge
     en voorgelezen aantal elkaar tegen. */
  const syncBadge = () => {
    const inner = document.querySelector('[data-cs-drawer-inner]');
    const bubble = document.getElementById('cart-icon-bubble');
    if (!inner || !bubble || inner.dataset.csTeller === undefined) return;
    const teller = inner.dataset.csTeller;
    const badge = bubble.querySelector('.cs-header__badge');
    if (badge) badge.textContent = teller;
    const srTekst = bubble.querySelector('.visually-hidden');
    if (srTekst) {
      srTekst.textContent = `Winkelwagen, ${teller} ${teller === '1' ? 'artikel' : 'artikelen'}`;
    }
  };

  /* Statusaankondiging voor screenreaders na elke ververste weergave. */
  const meldStatus = (inner) => {
    const statusEl = document.querySelector('[data-cs-drawer-status]');
    if (!statusEl || inner.dataset.csTeller === undefined) return;
    const teller = inner.dataset.csTeller;
    statusEl.textContent = `Winkelwagen bijgewerkt: ${teller} ${teller === '1' ? 'artikel' : 'artikelen'}.`;
  };

  /* ---------- Laadstaat (skeleton tot de eerste verse render) ---------- */

  /* Na ATC toont de drawer anders de oude, statisch gerenderde cart (op een
     verse pageload: "je winkelwagen is leeg") tot de mutaties klaar zijn.
     Refcount net als busy: een tweede snelle ATC mag de skeleton van de
     eerste finally niet kwijtraken. */
  const toonLaden = (aan) => {
    staat.laden = Math.max(0, staat.laden + (aan ? 1 : -1));
    const zichtbaar = staat.laden > 0;
    const p = paneel();
    const el = document.querySelector('[data-cs-drawer-laden]');
    if (p) p.classList.toggle('is-laden', zichtbaar);
    if (el) el.hidden = !zichtbaar;
  };

  /* ---------- Foutmelding ---------- */

  let foutTimer = 0;
  const toonFout = () => {
    const d = dialog();
    const el = document.querySelector('[data-cs-drawer-fout]');
    if (!d || !el) return;
    el.textContent = d.dataset.foutTekst || 'Er ging iets mis. Probeer het opnieuw.';
    el.hidden = false;
    clearTimeout(foutTimer);
    foutTimer = setTimeout(() => {
      el.hidden = true;
    }, 5000);
  };

  /* ---------- Open/sluit (zelfde patroon als de garantie-dialog) ---------- */

  const preventTouchWheel = (event) => {
    if (event.target === dialog()) event.preventDefault();
  };
  const onLockKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      sluit();
    }
  };
  const setLock = (locked) => {
    const method = locked ? 'addEventListener' : 'removeEventListener';
    document[method]('touchmove', preventTouchWheel, { passive: false });
    document[method]('wheel', preventTouchWheel, { passive: false });
    document[method]('keydown', onLockKeydown);
  };

  const gebonden = new WeakSet();
  const bindDialog = (d) => {
    if (gebonden.has(d)) return;
    gebonden.add(d);
    d.addEventListener('click', (event) => {
      if (event.target === d) sluit();
    });
    d.addEventListener('cancel', (event) => {
      event.preventDefault();
      sluit();
    });
    d.addEventListener('close', () => setLock(false));
  };

  /* Loopt gelijk met de transitieduur van het paneel in de CSS. */
  const SLUIT_MS = 380;

  /* Lost op zodra het paneel klaar is met binnenschuiven. `ververs` wacht
     hierop vóór de innerHTML-swap: na ATC opent de drawer met een skeleton en
     kwam de echte inhoud er middenin de slide overheen. Beweging bovenop
     beweging leest als verspringen. Het ophalen van de sections loopt tijdens
     het wachten gewoon door, dus dit kost geen extra tijd. */
  let slideKlaar = Promise.resolve();

  const open = (opener) => {
    const d = dialog();
    if (!d) return;
    bindDialog(d);
    if (opener) staat.opener = opener;
    if (d.open) return;
    staat.aanHetSluiten = false;
    d.showModal();
    setLock(true);
    /* Twee frames wachten in plaats van een reflow forceren: `void offsetWidth`
       legt de starttransform ook vast, maar dwingt een synchrone layout van de
       hele pagina af in precies het frame waarin de dialog naar de top layer
       gaat. Op de PDP is dat het duurste frame van de hele animatie. Na twee
       rAF's staat de starttransform net zo zeker vast, zonder die kosten. */
    /* Vangnet: in een achtergrondtab vuurt rAF niet. Zonder dit blijft het
       paneel dan buiten beeld staan terwijl de dialog wel open is. */
    let geopend = false;
    const zetOpen = () => {
      if (geopend) return;
      geopend = true;
      d.classList.add('is-open');
    };
    requestAnimationFrame(() => requestAnimationFrame(zetOpen));
    setTimeout(zetOpen, 120);



    const p = paneel();
    if (reducedMotion.matches || !p) return;
    slideKlaar = new Promise((klaar) => {
      let afgerond = false;
      const af = () => {
        if (afgerond) return;
        afgerond = true;
        p.removeEventListener('transitionend', opEinde);
        klaar();
      };
      const opEinde = (event) => {
        if (event.target === p && event.propertyName === 'transform') af();
      };
      p.addEventListener('transitionend', opEinde);
      /* Vangnet: blijft transitionend uit (achtergrondtab, of een browser die
         de transitie overslaat), dan mag de inhoud niet blijven hangen. */
      setTimeout(af, SLUIT_MS + 80);
    });
  };

  const sluit = () => {
    const d = dialog();
    if (!d || staat.aanHetSluiten || !d.open) return;
    staat.aanHetSluiten = true;
    const afronden = () => {
      staat.aanHetSluiten = false;
      d.close();
      if (staat.opener && document.contains(staat.opener)) staat.opener.focus();
      staat.opener = null;
    };
    d.classList.remove('is-open');
    if (reducedMotion.matches) {
      afronden();
    } else {
      setTimeout(afronden, SLUIT_MS);
    }
  };

  /* ---------- Queue: alle mutaties serieel, met busy-staat ---------- */

  /* Busy is een refcount: bij gequeuede taken mag de eerste finally de
     busy-staat niet weghalen terwijl de volgende taak nog moet draaien. */
  const inQueue = (taak) => {
    staat.busy += 1;
    const p = paneel();
    if (p) p.setAttribute('aria-busy', 'true');
    staat.queue = staat.queue
      .then(taak)
      .catch((fout) => {
        console.error('CS cart:', fout);
        toonFout();
        return ververs().catch(() => {});
      })
      .finally(() => {
        staat.busy -= 1;
        if (staat.busy > 0) return;
        const pk = paneel();
        if (pk) pk.removeAttribute('aria-busy');
      });
    return staat.queue;
  };

  /* ---------- Publieke API (gebruikt door cs-pdp-hero.js) ---------- */

  window.CSCart = {
    addAndOpen(formData, opener) {
      toonLaden(true);
      /* Direct openen. Er zat hier een pauze van 170ms zodat de ATC-knop eerst
         zijn toevoegen-staat kon tonen, maar die stilte tussen klik en
         beweging is precies wat als haperen leest: je tikt, er gebeurt niets,
         en dán schuift er iets. De knop verandert al bij de klik; dat is de
         bevestiging. */
      open(opener);

      return inQueue(async () => {
        /* Sections meeliften op de add: scheelt de aparte render-fetch. */
        formData.append('sections', sectieIds);
        const hoofd = await postForm(addUrl, formData);
        /* add.js geeft alleen de regel terug; voor het cadeau is de hele
           cart nodig. Wijzigt er niets, dan volstaan de meegelifte sections. */
        const gewijzigd = await reconcileCadeau(await fetchCart());
        await ververs(gewijzigd ? null : hoofd.sections || null);
      }).finally(() => toonLaden(false));
    },
    open(opener) {
      open(opener);
      return inQueue(async () => {
        await ververs();
      });
    },
    /* Voor de cart-pagina: cadeau gelijktrekken op een verse cart (zoals
       change.js die teruggeeft), zonder eigen render. */
    cadeauSync(cart) {
      return reconcileCadeau(cart);
    },
  };

  /* ---------- Interactie (delegatie: de inner wordt telkens vervangen) ---------- */

  document.addEventListener('click', (event) => {
    const el = event.target instanceof Element ? event.target : null;
    if (!el) return;

    const cartLink = el.closest('#cart-icon-bubble');
    if (cartLink && dialog()) {
      /* Modifier-clicks (nieuw tabblad e.d.) behouden het native linkgedrag. */
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();
      window.CSCart.open(cartLink);
      return;
    }

    if (el.closest('[data-cs-drawer-sluit]')) {
      sluit();
      return;
    }

    const legeCta = el.closest('[data-cs-leeg-cta]');
    if (legeCta) {
      const doel = new URL(legeCta.href, window.location.origin);
      if (doel.pathname === window.location.pathname) {
        event.preventDefault();
        sluit();
      }
      return;
    }

    const stap = el.closest('[data-cs-qty-plus], [data-cs-qty-min]');
    if (stap && !stap.disabled) {
      /* Alleen key/richting/bundle op event-tijd; de actuele quantity komt
         uit de verse cart binnen de queue-taak. Een snelle dubbele activatie
         (Enter/Space passeert pointer-events:none) telt zo wél twee keer op
         en een al verwijderde regel geeft geen 404-foutmelding. */
      const key = stap.dataset.key;
      const bundle = Math.max(parseInt(stap.dataset.bundle, 10) || 1, 1);
      const richting = stap.hasAttribute('data-cs-qty-plus') ? 1 : -1;
      inQueue(async () => {
        const cart = await fetchCart();
        const regel = (cart.items || []).find((i) => i.key === key);
        if (!regel) return;
        const nieuw = regel.quantity + richting * bundle;
        if (nieuw < bundle) return;
        const na = await post(changeUrl, { id: key, quantity: nieuw });
        await reconcileCadeau(na);
        await ververs();
      });
      return;
    }

    const verwijder = el.closest('[data-cs-verwijder]');
    if (verwijder) {
      const key = verwijder.dataset.key;
      inQueue(async () => {
        const cart = await fetchCart();
        const regel = (cart.items || []).find((i) => i.key === key);
        if (regel) {
          const na = await post(changeUrl, { id: key, quantity: 0 });
          await reconcileCadeau(na);
        }
        await ververs();
      });
      return;
    }

    /* Garantieregel opent de uitleg-pop-up van de productpagina. */
    const garantie = el.closest('[data-cs-garantie-uitleg]');
    if (garantie) {
      if (window.CSGarantie) window.CSGarantie.open('cart-drawer', garantie);
      return;
    }

    /* Pack-upsell: dezelfde eenmalige regel, alleen een groter aantal. Geen
       plan-wissel, dus de regel blijft eenmalig. */

    const verhoog = el.closest('[data-cs-verhoog-pack]');
    if (verhoog && verhoog.dataset.key) {
      const doelAantal = parseInt(verhoog.dataset.aantal, 10) || 0;
      if (doelAantal > 0) {
        inQueue(async () => {
          const cart = await fetchCart();
          const regel = (cart.items || []).find((i) => i.key === verhoog.dataset.key);
          if (!regel || regel.quantity >= doelAantal) return;
          const na = await post(changeUrl, { id: regel.key, quantity: doelAantal });
          await reconcileCadeau(na);
          await ververs();
        });
      }
      return;
    }
  });

  syncBadge();
  zetGarantieTrigger();

  /* bfcache-terugnavigatie herstelt de oude DOM; de cart kan intussen op een

     andere pagina gewijzigd zijn. Stil verversen houdt badge en drawer juist. */
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) ververs().catch(() => {});
  });

  /* Vangnet bij paginalading: een cart die buiten deze JS om is gewijzigd
     (checkout-terugknop, oude tab) krijgt alsnog het juiste cadeau. Alleen
     als er iets in de cart zit, anders is het een verspilde call. */
  const startTeller = document.querySelector('[data-cs-drawer-inner]');
  if (cadeauConfig() && startTeller && parseInt(startTeller.dataset.csTeller, 10) > 0) {
    inQueue(async () => {
      const gewijzigd = await reconcileCadeau(await fetchCart());
      if (gewijzigd) await ververs();
    });
  }

  /* Theme editor: drawer-sectie geselecteerd → drawer tonen (anders is de
     inhoud voor de merchant onbereikbaar); deselecteren → sluiten. Bij een
     sectie-rerender verdwijnt een open dialog zonder close-event: de
     scroll-lock expliciet loslaten, anders blijft Escape geblokkeerd. */
  document.addEventListener('shopify:section:select', (event) => {
    if (event.target.querySelector('[data-cs-cart-drawer]')) open();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    if (event.target.querySelector('[data-cs-cart-drawer]')) sluit();
  });
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.querySelector('[data-cs-cart-drawer]')) {
      setLock(false);
      staat.aanHetSluiten = false;
    }
  });
})();
